import React from "react";
import LegalPage from "@/pages/LegalPage";

const P = ({ children }) => <p>{children}</p>;
const L = ({ children }) => <ul className="list-disc pl-6 space-y-1.5 marker:text-secondary">{children}</ul>;
const Li = ({ children }) => <li>{children}</li>;
const B = ({ children }) => <span className="font-semibold text-ocean-700">{children}</span>;
const Note = ({ children }) => (
    <div className="rounded-xl bg-sand-100 border border-sand-200 p-4 text-sm text-ocean-700">
        {children}
    </div>
);
const Table = ({ head, rows }) => (
    <div className="overflow-x-auto rounded-xl border border-sand-200 my-2">
        <table className="w-full text-sm">
            <thead className="bg-sand-100 text-xs uppercase text-muted-foreground">
                <tr>{head.map((h, i) => <th key={i} className="text-left px-4 py-2 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-sand-200">
                {rows.map((r, i) => (
                    <tr key={i}>{r.map((c, j) => <td key={j} className="px-4 py-2.5 align-top">{c}</td>)}</tr>
                ))}
            </tbody>
        </table>
    </div>
);

const SECTIONS = [
    {
        id: "who-we-are",
        title: "Who we are",
        content: (
            <>
                <P>
                    This Privacy Policy explains how <B>Modern Business Solutions Ltd</B>, a company incorporated in the Cayman Islands (operating as <B>BlueLedger</B>; "<B>we</B>", "<B>us</B>", or "<B>our</B>"), collects, uses, shares, and safeguards personal data when you use the BlueLedger platform, websites, and related services (the "<B>Service</B>").
                </P>
                <P>
                    For the purposes of the Cayman Islands <B>Data Protection Act (2021 Revision)</B> (the "<B>DPA</B>") and other applicable data protection laws, we act as a <B>data controller</B> with respect to personal data about your account, billing, and interactions with our marketing; and as a <B>data processor</B> with respect to personal data that you or your organization upload into the Service as Customer Data (for example, your customers' and vendors' contact details). Your organization is the data controller of such Customer Data.
                </P>
                <Note>
                    <B>Data protection contact.</B> Email <a className="text-secondary-dark hover:underline" href="mailto:privacy@blueledger.io">privacy@blueledger.io</a>, or write to: Modern Business Solutions Ltd, Data Protection Officer, PO Box 1234, Grand Cayman, KY1-1103, Cayman Islands.
                </Note>
            </>
        ),
    },
    {
        id: "data-we-collect",
        title: "Personal data we collect",
        content: (
            <>
                <P>We collect the following categories of personal data:</P>
                <Table
                    head={["Category", "Examples", "Source"]}
                    rows={[
                        ["Account data", "Name, email address, password hash, profile picture, organization affiliation, role", "You provide it"],
                        ["Usage data", "Pages visited, features used, timestamps, IP address, device and browser information, language", "Automatically collected"],
                        ["Billing data", "Plan selection, billing cycle, payment events (processed by PayPal; we do not store card numbers)", "You and our payment processor"],
                        ["Content you upload (Customer Data)", "Invoices, bills, receipts, customer and vendor details, bank transaction metadata, journal entries, accounting records, documents and images", "You upload it"],
                        ["Communications", "Support emails, in-app messages, survey responses", "You provide it"],
                        ["Authentication data", "If you sign in with Google, the profile information Google provides (name, email, picture)", "Google, via OAuth"],
                        ["Cookies & similar tech", "Session cookies (httpOnly), security tokens, basic analytics identifiers", "Your browser"],
                    ]}
                />
                <P>
                    We do not knowingly collect personal data from children under 18. If you believe we have, contact us and we will delete it.
                </P>
            </>
        ),
    },
    {
        id: "how-we-use",
        title: "How we use personal data and our legal bases",
        content: (
            <>
                <P>
                    Under the DPA, we must rely on a lawful basis for each use of personal data. Our bases are:
                </P>
                <Table
                    head={["Purpose", "Lawful basis (DPA Schedule 2)"]}
                    rows={[
                        ["Create and manage your account; deliver the Service; provide customer support", "Performance of a contract"],
                        ["Process subscriptions and payments; detect and prevent fraud", "Performance of a contract; legitimate interests; legal obligation"],
                        ["Send transactional and security notifications", "Performance of a contract; legitimate interests"],
                        ["Improve, develop, and secure the Service (including debugging, aggregate analytics)", "Legitimate interests"],
                        ["Send marketing communications about BlueLedger", "Your consent (which you can withdraw any time) or legitimate interests where permitted"],
                        ["Comply with laws, court orders, and regulatory requests", "Legal obligation"],
                        ["Enforce our Terms, defend against claims, protect rights and property", "Legitimate interests"],
                        ["Process receipts via third-party AI vision providers on your instruction", "Performance of a contract; your organization as controller"],
                    ]}
                />
                <P>
                    We do not use your personal data for <B>automated decision-making that produces legal or similarly significant effects</B> without human review. Receipt OCR, transaction categorization, and similar suggestions are informational and subject to your review before posting.
                </P>
            </>
        ),
    },
    {
        id: "sharing",
        title: "How we share personal data",
        content: (
            <>
                <P>We share personal data only as described in this policy:</P>
                <L>
                    <Li>
                        <B>Sub-processors.</B> We use vetted service providers to operate the Service (cloud hosting, email delivery, payment processing, authentication, AI receipt OCR, customer support tooling, and analytics). They process personal data only on our documented instructions and under contractual confidentiality and security obligations. Current sub-processors are listed at <a href="/privacy#subprocessors" className="text-secondary-dark hover:underline">Section 9</a> below.
                    </Li>
                    <Li>
                        <B>Within your organization.</B> Other team members you invite can see Customer Data and activity within your organization according to the role-based access you configure.
                    </Li>
                    <Li>
                        <B>Legal and regulatory.</B> We may disclose personal data where required by law, court order, regulatory request, or to protect our rights, safety, or property, or those of our users or the public.
                    </Li>
                    <Li>
                        <B>Corporate transactions.</B> In the event of a merger, acquisition, financing, or sale of assets, personal data may be transferred to the successor or acquirer, subject to equivalent protections.
                    </Li>
                    <Li>
                        <B>With your consent or on your instruction.</B> For example, when you enable an integration with a third-party product.
                    </Li>
                </L>
                <P>
                    <B>We do not sell personal data.</B> We do not share personal data with third parties for their own marketing without your explicit consent.
                </P>
            </>
        ),
    },
    {
        id: "transfers",
        title: "International transfers",
        content: (
            <>
                <P>
                    The Service is operated globally and personal data may be transferred to, stored in, and processed in countries other than the Cayman Islands, including the United States, the European Union, and the United Kingdom. When we transfer personal data out of the Cayman Islands, we ensure an adequate level of protection consistent with Schedule 4 of the DPA, for example by:
                </P>
                <L>
                    <Li>Transferring to jurisdictions recognized by the Cayman Islands Ombudsman as offering adequate protection;</Li>
                    <Li>Entering into contractual safeguards (such as standard contractual clauses modelled on GDPR SCCs) with sub-processors;</Li>
                    <Li>Relying on your explicit consent, on contractual necessity, or on another derogation permitted by the DPA.</Li>
                </L>
                <P>You can request a copy of the safeguards we use by emailing <a className="text-secondary-dark hover:underline" href="mailto:privacy@blueledger.io">privacy@blueledger.io</a>.</P>
            </>
        ),
    },
    {
        id: "retention",
        title: "Retention and deletion",
        content: (
            <>
                <P>
                    We retain personal data only as long as necessary for the purposes described in this policy, or as required by law. Typical retention periods are:
                </P>
                <Table
                    head={["Data type", "Retention"]}
                    rows={[
                        ["Account data", "For as long as your account is active, plus up to 24 months after closure to resolve disputes, fraud, and comply with legal obligations"],
                        ["Customer Data uploaded into your organization", "Under your control; deleted on your request or shortly after account closure"],
                        ["Billing records and invoices issued by us to you", "Retained for periods required by applicable law and reasonable record-keeping"],
                        ["Audit logs", "30 days (Free), 180 days (Pro), 3 years (Team)"],
                        ["Receipt images and OCR output", "Until you delete them, or up to 24 months after account closure"],
                        ["Backups", "Rolling backups are retained up to 35 days and are deleted on the standard rotation schedule"],
                        ["Support correspondence", "Up to 36 months"],
                    ]}
                />
                <P>
                    On verified deletion requests, we will erase personal data without undue delay, subject to limited retention for legal, regulatory, or legitimate interest reasons (which we will describe on request).
                </P>
            </>
        ),
    },
    {
        id: "rights",
        title: "Your rights under the Cayman DPA",
        content: (
            <>
                <P>If you are a data subject whose personal data we process, you have the following rights under the DPA:</P>
                <L>
                    <Li><B>The right to be informed</B> — to know how your data is used (this policy).</Li>
                    <Li><B>The right of access</B> — to obtain confirmation of processing and a copy of your personal data.</Li>
                    <Li><B>The right to rectification</B> — to have inaccurate data corrected.</Li>
                    <Li><B>The right to erasure</B> — to request deletion where processing has no lawful basis, consent is withdrawn, or the data is no longer needed.</Li>
                    <Li><B>The right to stop or restrict processing</B> — including for direct marketing, on request (we will comply within 21 days).</Li>
                    <Li><B>The right to data portability</B> — to receive your data in a structured, commonly used, machine-readable format (CSV exports are available in-app).</Li>
                    <Li><B>The right to object to automated decisions</B> — to request human review of any significant decision made solely by automated means.</Li>
                    <Li><B>The right to complain</B> — to the Cayman Islands Ombudsman (see Section 11).</Li>
                </L>
                <P>
                    To exercise any right, email <a className="text-secondary-dark hover:underline" href="mailto:privacy@blueledger.io">privacy@blueledger.io</a>. We may need to verify your identity before acting on a request. We will respond without undue delay and, where the DPA imposes a specific timeframe, within that period.
                </P>
                <P>
                    Where we act as a <B>processor</B> of Customer Data on behalf of your organization, requests should normally be directed to that organization (the controller), which can then instruct us.
                </P>
            </>
        ),
    },
    {
        id: "security",
        title: "Security",
        content: (
            <>
                <P>
                    We implement appropriate technical and organizational measures to protect personal data against unauthorized access, alteration, disclosure, or destruction, including:
                </P>
                <L>
                    <Li>Encryption in transit (TLS) and at rest for sensitive data stores;</Li>
                    <Li>Salted and hashed passwords (bcrypt);</Li>
                    <Li>HttpOnly, Secure, SameSite cookies for session authentication;</Li>
                    <Li>Role-based access control (RBAC) within each organization;</Li>
                    <Li>Tenant isolation of Customer Data at the application layer;</Li>
                    <Li>Least-privilege access for engineering and support staff, with logging;</Li>
                    <Li>Regular backups, patching, and monitoring for anomalous activity;</Li>
                    <Li>Periodic review of sub-processors' security posture.</Li>
                </L>
                <P>
                    No system is perfectly secure. You are responsible for using strong, unique passwords, enabling multi-factor authentication where available, and keeping your devices secure.
                </P>
            </>
        ),
    },
    {
        id: "subprocessors",
        title: "Sub-processors",
        content: (
            <>
                <P>We currently use (or may use) the following categories of sub-processors. We update this list when material changes occur.</P>
                <Table
                    head={["Sub-processor", "Purpose", "Location"]}
                    rows={[
                        ["Cloud hosting provider (e.g., AWS / Google Cloud)", "Application hosting, databases, storage", "United States and global edge"],
                        ["OpenAI", "Receipt OCR via GPT-4o vision (on your instruction)", "United States"],
                        ["PayPal", "Subscription payment processing", "Global"],
                        ["Google (Google OAuth)", "Optional social sign-in", "Global"],
                        ["Mailgun (or successor ESP)", "Transactional and notification email", "United States / European Union"],
                        ["Error monitoring & analytics (aggregate, pseudonymised)", "Service reliability and product analytics", "Global"],
                    ]}
                />
            </>
        ),
    },
    {
        id: "breaches",
        title: "Data breach notification",
        content: (
            <>
                <P>
                    In accordance with the DPA, we will notify the Cayman Islands Ombudsman of any personal data breach that is reasonably likely to result in damage, distress, or significant harm to an individual within <B>5 days</B> of becoming aware of it, and will notify affected individuals without undue delay where legally required. We maintain internal procedures for detecting, escalating, and responding to security incidents.
                </P>
            </>
        ),
    },
    {
        id: "complaints",
        title: "Complaints and supervisory authority",
        content: (
            <>
                <P>
                    If you have a concern about our handling of your personal data, please contact us first at <a className="text-secondary-dark hover:underline" href="mailto:privacy@blueledger.io">privacy@blueledger.io</a>. If you are not satisfied, you have the right to lodge a complaint with the Cayman Islands supervisory authority:
                </P>
                <Note>
                    <B>Office of the Ombudsman (Cayman Islands)</B><br />
                    PO Box 2252, Grand Cayman, KY1-1107<br />
                    Website: <a className="text-secondary-dark hover:underline" href="https://ombudsman.ky" target="_blank" rel="noreferrer">ombudsman.ky</a><br />
                    Email: <a className="text-secondary-dark hover:underline" href="mailto:info@ombudsman.ky">info@ombudsman.ky</a>
                </Note>
                <P>
                    If you are located elsewhere, you may also contact your local data protection authority.
                </P>
            </>
        ),
    },
    {
        id: "cookies",
        title: "Cookies and similar technologies",
        content: (
            <>
                <P>We use a small number of cookies, strictly for:</P>
                <L>
                    <Li><B>Authentication</B> — httpOnly session and refresh cookies, without which the Service cannot function.</Li>
                    <Li><B>Security</B> — CSRF and rate-limit tokens.</Li>
                    <Li><B>Preferences</B> — remembering your selected organization.</Li>
                </L>
                <P>
                    We do not use advertising cookies and we do not share personal data with ad networks. You can disable cookies in your browser, but the Service will not function without essential cookies.
                </P>
            </>
        ),
    },
    {
        id: "changes",
        title: "Changes to this policy",
        content: (
            <>
                <P>
                    We may update this Privacy Policy from time to time. If the changes are material, we will notify you via email or through the Service before the changes take effect. We encourage you to review this policy periodically. The "Effective date" at the top reflects the current version.
                </P>
            </>
        ),
    },
    {
        id: "contact",
        title: "Contact",
        content: (
            <>
                <P>
                    For any question about this policy or our privacy practices:
                </P>
                <Note>
                    Modern Business Solutions Ltd — Data Protection Officer<br />
                    Email: <a className="text-secondary-dark hover:underline" href="mailto:privacy@blueledger.io">privacy@blueledger.io</a><br />
                    Post: PO Box 1234, Grand Cayman, KY1-1103, Cayman Islands
                </Note>
            </>
        ),
    },
];

export default function PrivacyPolicy() {
    return (
        <LegalPage
            title="Privacy Policy"
            effectiveDate="1 May 2026"
            version="1.0"
            sections={SECTIONS}
        />
    );
}
